import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, BarChart3 } from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { API, TEAL } from './domainUtils';
import { SitePerformancePanel } from './SitePerformancePanel';

export const DomainAnalyticsTab = ({ domainId, token, onSwitchTab }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    axios.get(`${API}/analytics/executive?domain_id=${domainId}`, { headers })
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, [domainId, headers]);

  if (loading) return (
    <div className="flex justify-center py-12">
      <Loader2 className="w-7 h-7 text-white/20 animate-spin" />
    </div>
  );

  const kpis = data?.kpis || {};
  const trend = data?.weekly_trend || [];
  const distribution = data?.score_distribution || [];
  const typeBreakdown = data?.type_breakdown || [];

  if (!kpis.total_calls) return (
    <div className="text-center py-16 text-white/20">
      <BarChart3 className="w-10 h-10 mx-auto mb-3" />
      <p>No graded calls yet. Sync and grade calls to see analytics.</p>
    </div>
  );

  const kpiCards = [
    { label: 'Total Graded Calls', value: kpis.total_calls, color: 'text-white', statusFilter: null, hint: 'All calls' },
    { label: 'Average Score', value: `${kpis.avg_score}%`, color: kpis.avg_score >= 70 ? 'text-emerald-400' : 'text-amber-400', statusFilter: null, hint: null },
    { label: 'Escalated Calls', value: kpis.escalated_calls, color: 'text-red-400', statusFilter: 'escalated', hint: 'Click to view' },
    { label: 'Active Agents', value: kpis.active_agents, color: 'text-teal-400', statusFilter: null, hint: null },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpiCards.map(k => (
          <div
            key={k.label}
            onClick={() => {
              if (!onSwitchTab) return;
              if (k.statusFilter !== null) onSwitchTab('calls', k.statusFilter);
              else if (k.hint === 'All calls') onSwitchTab('calls', '');
            }}
            className={`bg-white/5 border border-white/10 rounded-xl p-4 ${
              (k.statusFilter !== null || k.hint === 'All calls') && onSwitchTab
                ? 'cursor-pointer hover:bg-white/10 hover:border-teal-500/30 transition-colors'
                : ''
            }`}
            data-testid={`analytics-kpi-${k.label.toLowerCase().replace(/ /g, '-')}`}
          >
            <p className={`text-2xl font-bold ${k.color}`}>{k.value}</p>
            <p className="text-white/30 text-xs mt-0.5">{k.label}</p>
            {k.hint && onSwitchTab && <p className="text-teal-400/60 text-xs mt-1">{k.hint} →</p>}
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white/5 border border-white/10 rounded-xl p-5">
          <p className="text-white font-medium text-sm mb-4">Weekly Score Trend</p>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={trend}>
              <defs>
                <linearGradient id="gradScore" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={TEAL} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={TEAL} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="week" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }} axisLine={false} tickLine={false} width={30} />
              <Tooltip contentStyle={{ background: '#0A0F1C', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff' }} />
              <Area type="monotone" dataKey="avg_score" stroke={TEAL} fill="url(#gradScore)" strokeWidth={2} dot={false} name="Avg Score" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-xl p-5">
          <p className="text-white font-medium text-sm mb-4">Score Distribution</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={distribution} barSize={32}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="range" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }} axisLine={false} tickLine={false} width={30} />
              <Tooltip contentStyle={{ background: '#0A0F1C', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff' }} />
              <Bar dataKey="count" fill={TEAL} radius={[4, 4, 0, 0]} name="Calls" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {typeBreakdown.length > 0 && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-5">
          <p className="text-white font-medium text-sm mb-4">
            Performance by Call Type{' '}
            <span className="text-white/30 text-xs font-normal">(click row to filter calls)</span>
          </p>
          <div className="space-y-3">
            {typeBreakdown.map(t => (
              <div
                key={t.call_type}
                onClick={() => onSwitchTab && onSwitchTab('calls', t.call_type === 'escalated' ? 'escalated' : '')}
                className={`flex items-center gap-3 rounded-lg px-2 py-1 -mx-2 transition-colors ${onSwitchTab ? 'cursor-pointer hover:bg-white/5' : ''}`}
              >
                <span className="text-white/60 text-sm w-56 truncate" title={t.call_type.replace(/_/g, ' ')}>{t.call_type.replace(/_/g, ' ')}</span>
                <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${t.avg_score}%`, backgroundColor: TEAL }} />
                </div>
                <span className="text-teal-400 text-sm font-semibold w-12 text-right">{t.avg_score}%</span>
                <span className="text-white/30 text-xs w-14 text-right">
                  {t.count} calls{onSwitchTab ? <span className="text-teal-500/50 ml-1">→</span> : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <SitePerformancePanel domainId={domainId} token={token} days={30} />
    </div>
  );
};
